import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Package, ShoppingCart, CheckCircle, Clock, Download, Eye, Check, Mail, Pencil, Save, X, Plus, Minus, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const BestellHistorie = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // Neu: Status-Filter
  const [viewOrder, setViewOrder] = useState(null); // Für Ansicht-Dialog
  const [editMode, setEditMode] = useState(false);  // Bearbeitungsmodus
  const [editItems, setEditItems] = useState([]);   // Kopie der Items für Edit
  const [editNotes, setEditNotes] = useState('');   // Kopie der Notiz für Edit
  const [savingEdit, setSavingEdit] = useState(false);
  const [completeOrder, setCompleteOrder] = useState(null); // Für Als-Erledigt-Dialog
  const [wwsReference, setWwsReference] = useState(''); // WWS Referenz

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/api/orders`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/api/order-categories`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      setOrders(ordersRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (orderId, categoryName) => {
    try {
      const response = await axios.get(
        `${API}/api/orders/${orderId}/pdf`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Bestellung_${categoryName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF wurde heruntergeladen');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Fehler beim Herunterladen des PDFs');
    }
  };

  const handleCompleteOrder = async () => {
    if (!completeOrder) return;
    try {
      await axios.put(
        `${API}/api/orders/${completeOrder.id}/complete`,
        null,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          params: { wws_reference: wwsReference || undefined }
        }
      );
      toast.success('Bestellung als erledigt markiert');
      setCompleteOrder(null);
      setWwsReference('');
      fetchData();
    } catch (error) {
      console.error('Failed to complete order:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Abschließen der Bestellung');
    }
  };

  const openEditMode = (order) => {
    setEditItems(order.items.map(i => ({ ...i })));
    setEditNotes(order.notes || '');
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!viewOrder) return;
    setSavingEdit(true);
    try {
      const res = await axios.put(
        `${API}/api/orders/${viewOrder.id}`,
        { items: editItems, notes: editNotes },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Bestellung aktualisiert');
      setViewOrder(res.data);
      setEditMode(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingEdit(false);
    }
  };

  const changeQty = (idx, delta) => {
    setEditItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, quantity: Math.max(0, (item.quantity || 1) + delta) } : item
    ));
  };

  const removeItem = (idx) => {
    setEditItems(prev => prev.filter((_, i) => i !== idx));
  };

  const filteredOrders = orders.filter(o => {
    const categoryMatch = filterCategory === 'all' || o.category_id === filterCategory;
    const statusMatch = filterStatus === 'all' || o.status === filterStatus;
    return categoryMatch && statusMatch;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Lädt...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 pb-6">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Bestellhistorie
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">
            Übersicht über alle Bestellungen
          </p>
        </div>

        {/* Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="filter-category" className="text-sm mb-2 block">Filter nach Kategorie</Label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger id="filter-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Kategorien</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="filter-status" className="text-sm mb-2 block">Filter nach Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger id="filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="open">🟡 Offen</SelectItem>
                    <SelectItem value="completed">🟢 In WWS übertragen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Noch keine Bestellungen</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Sie haben noch keine Bestellungen aufgegeben
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((order) => {
              const orderDate = new Date(order.created_at);
              const totalItems = order.items.reduce((sum, item) => sum + item.quantity, 0);
              
              return (
                <Card key={order.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="text-3xl flex-shrink-0">
                            {categories.find(c => c.id === order.category_id)?.icon || '📦'}
                          </div>
                          <div>
                            <h3 className="font-semibold text-base sm:text-lg">{order.category_name}</h3>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1">
                              {orderDate.toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        
                        <Badge 
                          variant="outline" 
                          className={`w-fit ${
                            order.status === 'completed' 
                              ? 'bg-green-100 text-green-700 border-green-300' 
                              : 'bg-yellow-100 text-yellow-700 border-yellow-300'
                          }`}
                        >
                          {order.status === 'completed' ? (
                            <>
                              🟢 In WWS übertragen
                            </>
                          ) : (
                            <>
                              🟡 Offen
                            </>
                          )}
                        </Badge>
                      </div>

                      {/* Items Summary */}
                      <div className="space-y-1.5 border-t pt-3">
                        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Bestellte Produkte:</div>
                        {order.items.map((item, idx) => {
                          const artNr = item.internal_article_number || item.supplier_article_number;
                          return (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <span className="text-gray-800 dark:text-gray-200">
                                <span className="font-semibold">{item.quantity}×</span> {item.product_name}
                              </span>
                              {artNr && (
                                <span className="text-xs font-mono text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded ml-2 shrink-0">
                                  {item.internal_article_number ? `INT: ${item.internal_article_number}` : `Nr: ${item.supplier_article_number}`}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Notes */}
                      {order.notes && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-900">
                            <strong>Notiz:</strong> {order.notes}
                          </p>
                        </div>
                      )}
                      
                      {/* WWS Reference (wenn vorhanden) */}
                      {order.wws_reference && (
                        <div className="p-3 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-lg">
                          <p className="text-sm text-green-900 dark:text-green-300">
                            <strong>WWS-Referenz:</strong> {order.wws_reference}
                          </p>
                        </div>
                      )}

                      {/* Standort Info */}
                      {order.location_name && (
                        <div className="text-sm text-gray-500">
                          📍 Standort: <span className="font-medium">{order.location_name}</span>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="pt-3 border-t space-y-3">
                        {/* WWS-Nummer direkt eingeben (offen + kein Referenz vorhanden) */}
                        {order.status === 'open' && (
                          <div className="flex gap-2 items-center p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                            <div className="flex-1 space-y-1">
                              <Label className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                                In WWS/Extern bestellt? Bestellnummer eintragen:
                              </Label>
                              <Input
                                placeholder="z.B. WWS-12345 oder Bestellnr."
                                defaultValue=""
                                id={`wws-${order.id}`}
                                className="h-8 text-sm font-mono"
                                data-testid={`wws-input-${order.id}`}
                              />
                            </div>
                            <Button
                              size="sm"
                              className="shrink-0 bg-green-600 hover:bg-green-700 mt-4"
                              onClick={async () => {
                                const input = document.getElementById(`wws-${order.id}`);
                                const ref = input?.value?.trim() || '';
                                try {
                                  await axios.put(
                                    `${API}/api/orders/${order.id}/complete`,
                                    null,
                                    {
                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                      params: { wws_reference: ref || undefined }
                                    }
                                  );
                                  toast.success(`Bestellung erledigt${ref ? ` · Ref: ${ref}` : ''}`);
                                  fetchData();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Fehler');
                                }
                              }}
                              data-testid={`complete-order-${order.id}`}
                            >
                              <Check className="h-4 w-4 mr-1" /> Erledigt
                            </Button>
                          </div>
                        )}
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Button onClick={() => setViewOrder(order)} variant="outline" className="flex-1" size="sm">
                            <Eye className="h-4 w-4 mr-2" /> Ansicht
                          </Button>
                          <Button onClick={() => handleDownloadPDF(order.id, order.category_name)} variant="outline" className="flex-1" size="sm">
                            <Download className="h-4 w-4 mr-2" /> PDF
                          </Button>
                          {/* Manueller Lieferanten-Versand – nur wenn supplier_email gesetzt */}
                          {categories.find(c => c.id === order.category_id)?.supplier_email && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/20 gap-1.5"
                              onClick={async () => {
                                try {
                                  const res = await axios.post(
                                    `${API}/api/orders/${order.id}/send-to-supplier`,
                                    {},
                                    { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
                                  );
                                  toast.success(`PDF gesendet an ${res.data.sent_to}`);
                                  fetchData();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Fehler beim Senden');
                                }
                              }}
                              data-testid={`send-supplier-${order.id}`}
                            >
                              <Mail className="h-4 w-4" /> An Lieferant
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Ansicht Dialog */}
        <Dialog open={!!viewOrder} onOpenChange={() => { setViewOrder(null); setEditMode(false); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editMode ? <Pencil className="h-4 w-4 text-primary" /> : <Eye className="h-4 w-4" />}
                {editMode ? 'Bestellung bearbeiten' : 'Bestelldetails'}
              </DialogTitle>
              <DialogDescription>
                {editMode
                  ? 'Mengen anpassen, Artikel entfernen oder Notiz bearbeiten'
                  : 'Vollständige Übersicht der Bestellung'}
              </DialogDescription>
            </DialogHeader>
            
            {viewOrder && (
              <div className="space-y-4">
                {/* Order Info */}
                <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm">
                  <div>
                    <p className="text-gray-500">Kategorie</p>
                    <p className="font-semibold">{viewOrder.category_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Datum</p>
                    <p className="font-semibold">
                      {new Date(viewOrder.created_at).toLocaleDateString('de-DE', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    <Badge variant="outline" className={`w-fit ${
                      viewOrder.status === 'completed'
                        ? 'bg-green-100 text-green-700 border-green-300'
                        : 'bg-yellow-100 text-yellow-700 border-yellow-300'
                    }`}>
                      {viewOrder.status === 'completed' ? '🟢 In WWS übertragen' : '🟡 Offen'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-500">Standort</p>
                    <p className="font-semibold">{viewOrder.location_name || 'Unbekannt'}</p>
                  </div>
                </div>

                {/* Products – Edit Mode */}
                <div>
                  <h4 className="font-semibold mb-3">Bestellte Produkte</h4>
                  {editMode ? (
                    <div className="space-y-2">
                      {editItems.length === 0 && (
                        <p className="text-sm text-destructive text-center py-2">Mindestens 1 Artikel erforderlich</p>
                      )}
                      {editItems.map((item, idx) => (
                        <div key={idx} className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                          (item.quantity || 0) === 0 ? 'opacity-40 bg-red-50 border-red-200' : 'bg-background border-border'
                        }`}>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm leading-snug">{item.product_name}</p>
                            {(item.internal_article_number || item.supplier_article_number) && (
                              <p className="text-xs font-mono text-blue-600 mt-0.5">
                                {item.internal_article_number ? `JET: ${item.internal_article_number}` : `Nr.: ${item.supplier_article_number}`}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button type="button" onClick={() => changeQty(idx, -1)}
                              className="h-7 w-7 rounded-lg border flex items-center justify-center hover:bg-muted transition-colors">
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-8 text-center font-bold tabular-nums text-sm">{item.quantity}</span>
                            <button type="button" onClick={() => changeQty(idx, 1)}
                              className="h-7 w-7 rounded-lg border flex items-center justify-center hover:bg-muted transition-colors">
                              <Plus className="h-3 w-3" />
                            </button>
                            <button type="button" onClick={() => removeItem(idx)}
                              className="h-7 w-7 rounded-lg text-destructive hover:bg-red-50 flex items-center justify-center ml-1 transition-colors">
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {viewOrder.items.map((item, idx) => (
                        <div key={idx} className="flex items-start justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium leading-snug">{item.product_name}</p>
                            {(item.internal_article_number || item.supplier_article_number) && (
                              <p className="text-xs font-mono text-blue-600 dark:text-blue-400 mt-0.5">
                                {item.internal_article_number ? `JET: ${item.internal_article_number}` : `Nr.: ${item.supplier_article_number}`}
                              </p>
                            )}
                            {item.packaging_unit && (
                              <p className="text-xs text-gray-500 mt-0.5">VE: {item.packaging_unit}</p>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                            <span className="font-bold text-base tabular-nums">{item.quantity}</span>
                            <p className="text-xs text-gray-500">Menge</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes */}
                {editMode ? (
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold">Notiz</Label>
                    <Textarea
                      value={editNotes}
                      onChange={e => setEditNotes(e.target.value)}
                      placeholder="Optionale Notiz zur Bestellung..."
                      rows={2}
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                ) : viewOrder.notes ? (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-semibold text-blue-900 mb-1">Notiz:</p>
                    <p className="text-sm text-blue-900">{viewOrder.notes}</p>
                  </div>
                ) : null}

                {/* WWS Reference */}
                {!editMode && viewOrder.wws_reference && (
                  <div className="p-3 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-lg">
                    <p className="text-sm font-semibold text-green-900 dark:text-green-300 mb-1">WWS-Referenz:</p>
                    <p className="text-sm text-green-900 dark:text-green-300">{viewOrder.wws_reference}</p>
                  </div>
                )}

                {/* Edit-Hinweis wenn abgeschlossen */}
                {!editMode && viewOrder.status === 'completed' && (
                  <p className="text-xs text-muted-foreground text-center">Abgeschlossene Bestellungen können nicht mehr bearbeitet werden.</p>
                )}
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              {editMode ? (
                <>
                  <Button variant="outline" onClick={() => setEditMode(false)} className="gap-1.5">
                    <X className="h-4 w-4" /> Abbrechen
                  </Button>
                  <Button onClick={handleSaveEdit} disabled={savingEdit || editItems.length === 0 || editItems.every(i => i.quantity === 0)} className="gap-1.5">
                    {savingEdit ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <Save className="h-4 w-4" />}
                    Änderungen speichern
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => { setViewOrder(null); setEditMode(false); }}>
                    Schließen
                  </Button>
                  {viewOrder?.status !== 'completed' && (
                    <Button onClick={() => openEditMode(viewOrder)} className="gap-1.5">
                      <Pencil className="h-4 w-4" /> Bestellung bearbeiten
                    </Button>
                  )}
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Als Erledigt Dialog */}
        <Dialog open={!!completeOrder} onOpenChange={() => {
          setCompleteOrder(null);
          setWwsReference('');
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Bestellung als erledigt markieren</DialogTitle>
              <DialogDescription>
                Bestätigen Sie, dass diese Bestellung in WWS übertragen wurde
              </DialogDescription>
            </DialogHeader>
            
            {completeOrder && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <strong>Kategorie:</strong> {completeOrder.category_name}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <strong>Produkte:</strong> {completeOrder.items.length}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Datum:</strong> {new Date(completeOrder.created_at).toLocaleDateString('de-DE')}
                  </p>
                </div>

                <div>
                  <Label htmlFor="wws-ref" className="text-sm mb-2 block">
                    WWS-Referenznummer (optional)
                  </Label>
                  <Input
                    id="wws-ref"
                    placeholder="z.B. WWS-2025-12345"
                    value={wwsReference}
                    onChange={(e) => setWwsReference(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Geben Sie eine Referenznummer ein für die interne Nachverfolgung
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setCompleteOrder(null);
                  setWwsReference('');
                }}
              >
                Abbrechen
              </Button>
              <Button 
                onClick={handleCompleteOrder}
                className="bg-green-600 hover:bg-green-700"
              >
                Als erledigt markieren
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
